<template>
  <div>
    <v-card rounded="lg">
      <div class="d-flex gap-2 align-center pa-4">
        <v-btn @click="changeMonth(-1)" x-small fab color="primary" :disabled="loading">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-text-field
          v-model="startDate"
          outlined
          dense
          label="Data de início"
          type="date"
          hide-details
          class="flex-grow-0"
          style="width: 170px"
          :disabled="loading"
          @keyup.enter="getPayments"
        />
        <v-text-field
          v-model="endDate"
          outlined
          dense
          label="Data de fim"
          type="date"
          hide-details
          class="flex-grow-0"
          style="width: 170px"
          :disabled="loading"
          @keyup.enter="getPayments"
        />
        <v-btn @click="changeMonth(1)" x-small fab color="primary" :disabled="loading">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-spacer />
        <v-btn @click="getPayments" color="primary" :loading="loading" :disabled="loading"> Atualizar </v-btn>
      </div>
      <v-checkbox
        :input-value="searchBy === 'payDate'"
        @click="searchBy = searchBy === 'payDate' ? 'validity' : 'payDate'"
        label="Buscar por data de pagamento"
        class="ma-0 pa-0 ml-4"
        hide-details
        dense
      />

      <v-card class="d-flex flex-wrap justify-space-around pa-4">
        <div class="d-flex flex-column align-center justify-center px-4">
          <v-skeleton-loader v-if="loading" type="card" width="100px" height="22px" class="mb-2" />
          <h5 v-else class="text-center" style="font-size: 1.3rem">{{ resume.totalAmount | currency }}</h5>
          <span class="lh-1">
            {{ loading ? '' : resume.totalCount }} emitid{{ resume.totalCount != 1 || loading ? 'os' : 'o' }}
          </span>
        </div>
        <div class="d-flex flex-column align-center justify-center px-4">
          <v-skeleton-loader v-if="loading" type="card" width="100px" height="22px" class="mb-2" />
          <h5
            v-else
            class="text-center"
            style="font-size: 1.3rem"
            :style="{ color: resume.paidAmount > 0 ? 'var(--v-success-base) !important' : '' }"
          >
            {{ resume.paidAmount | currency }}
          </h5>
          <span class="lh-1">
            {{ loading ? '' : resume.paidCount }} pago{{ resume.paidCount != 1 || loading ? 's' : '' }}
          </span>
        </div>
        <div class="d-flex flex-column align-center justify-center px-4">
          <v-skeleton-loader v-if="loading" type="card" width="100px" height="22px" class="mb-2" />
          <h5
            v-else
            class="text-center"
            style="font-size: 1.3rem"
            :style="{ color: resume.pendingAmount > 0 ? 'var(--v-info-base) !important' : '' }"
          >
            {{ resume.pendingAmount | currency }}
          </h5>
          <span class="lh-1">
            {{ loading ? '' : resume.pendingCount }} pendent{{ resume.pendingCount != 1 || loading ? 'es' : 'e' }}
          </span>
        </div>
        <div class="d-flex flex-column align-center justify-center px-4">
          <v-skeleton-loader v-if="loading" type="card" width="100px" height="22px" class="mb-2" />
          <h5
            v-else
            class="text-center"
            style="font-size: 1.3rem"
            :style="{ color: resume.overdueAmount > 0 ? 'var(--v-error-base) !important' : '' }"
          >
            {{ resume.overdueAmount | currency }}
          </h5>
          <span class="lh-1">
            {{ loading ? '' : resume.overdueCount }} vencid{{ resume.overdueCount != 1 || loading ? 'os' : 'o' }}
          </span>
        </div>
      </v-card>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="7" />
      <div v-else class="d-flex justify-center overflow-hidden" style="height: 7px">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="d-inline-block success"
              :style="{
                width: (resume.paidAmount / resume.totalAmount) * 100 + '%',
                height: '100%',
              }"
            />
          </template>
          <span>{{ resume.paidAmount | currency }} pago</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div
              class="d-inline-block error"
              :style="{
                width: (resume.overdueAmount / resume.totalAmount) * 100 + '%',
                height: '100%',
              }"
            />
          </template>
          <span>{{ resume.overdueAmount | currency }} vencido</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="d-inline-block info"
              :style="{
                width: (resume.pendingAmount / resume.totalAmount) * 100 + '%',
                height: '100%',
              }"
            />
          </template>
          <span>{{ resume.pendingAmount | currency }} pendente</span>
        </v-tooltip>
      </div>

      <v-alert v-if="error" type="error">{{ error }} </v-alert>
      <v-data-table v-else :headers="headers" :items="payments" :items-per-page="10" sort-by="validity">
        <template v-slot:[`item.id`]="{ item }">
          {{ item.id.slice(0, 8) }}
        </template>
        <template v-slot:[`item.validity`]="{ item }">
          {{ item.validity | date }}
        </template>
        <template v-slot:[`item.value`]="{ item }">
          {{ item.value | currency }}
          <small v-if="item.statusDefinition.key === 'paidOverdue' && item.penalty > 0" class="d-block">
            +{{ item.penalty | currency }} multa
          </small>
          <small v-if="item.statusDefinition.key === 'paidOverdue' && item.fees > 0" class="d-block">
            +{{ item.fees | currency }} juros
          </small>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="item.statusDefinition.color" small label>
            {{ item.statusDefinition.text }} {{ item.paymentMethod }}
          </v-chip>
          <small class="d-block text-center" v-if="item.payDate">
            {{ item.payDate | date }}
          </small>
        </template>
        <template v-slot:[`item.gateway`]="{ item }">
          <v-chip v-if="item.gateway == 'Externo'" color="primary" small label>
            {{ item.Bank?.name || paymentDefinitions.gateways[item.gateway]?.text }}
          </v-chip>
          <v-chip v-else-if="item.gateway" :color="paymentDefinitions.gateways[item.gateway]?.color" small label>
            {{ paymentDefinitions.gateways[item.gateway]?.text }}
          </v-chip>
          <v-chip v-else color="error" small label> Não emitido </v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                x-small
                color="primary"
                @click="downloadItem(item)"
                :loading="loadingDownload === item.id"
                :disabled="!!loadingDownload || ['Paid', 'Canceled'].includes(item.status)"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Ver Pix e Boleto</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon x-small color="primary" @click="goToRental(item)">
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </template>
            <span>Ver contrato</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
    <boleto />
  </div>
</template>

<script>
import moment from 'moment'
import REPORTS from '@/services/report'
import rentalService from '@/services/rental'
import paymentDefinitions from '@/definitions/payment.js'
import Boleto from '@/components/modals/Boleto.vue'

export default {
  metaInfo: {
    title: 'Pagamentos',
  },
  name: 'Payments',
  components: {
    Boleto,
  },
  data() {
    return {
      loading: false,
      loadingDownload: null,
      error: null,
      payments: [],
      startDate: null,
      endDate: null,
      paymentDefinitions,
      refreshTimeout: null,
      searchBy: 'validity',
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Data de validade', value: 'validity' },
        { text: 'Valor', value: 'value' },
        { text: 'Cliente', value: 'client.name' },
        { text: 'Gateway', value: 'gateway', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Ações', value: 'actions', align: 'center', sortable: false },
      ],
    }
  },
  methods: {
    async getPayments() {
      if (this.loading) return
      this.loading = true
      try {
        const response = await REPORTS.payments({
          startDate: this.startDate,
          endDate: this.endDate,
          searchBy: this.searchBy,
        })
        this.payments = response.payments.map(payment => ({
          ...payment,
          value: parseFloat(payment.value),
          penalty: parseFloat(payment.penalty),
          fees: parseFloat(payment.fees),
          discount: parseFloat(payment.discount),
          statusDefinition: paymentDefinitions.getPaymentStatus(payment),
          client: payment.contract.Rental.Clients[0].client,
        }))
      } catch (error) {
        this.error = error.message || 'Erro ao buscar pagamentos'
      } finally {
        this.loading = false
      }
    },
    async downloadItem(item) {
      try {
        this.loadingDownload = item.id
        const rental = item.contract.Rental
        let response = await rentalService.getBoleto(rental.id, item.id)
        this.$root.$emit('show-boleto', response)
      } catch (e) {
        this.$toast.fire({
          title: e.message || 'Erro ao gerar boleto',
          icon: 'error',
        })
      } finally {
        this.loadingDownload = null
      }
    },
    goToRental(item) {
      this.$router.push({
        name: 'rental.edit',
        params: { id: item.contract.Rental.id },
        hash: '#payment',
      })
    },
    changeMonth(direction) {
      this.startDate = moment(this.startDate).add(direction, 'month').startOf('month').format('YYYY-MM-DD')
      this.endDate = moment(this.endDate).add(direction, 'month').endOf('month').format('YYYY-MM-DD')
      this.getPayments()
    },
  },
  computed: {
    resume() {
      return this.payments.reduce(
        (acc, payment) => {
          if (payment.statusDefinition.key === 'canceled') return acc

          acc.totalAmount += parseFloat(payment.value)
          acc.totalCount++
          if (payment.statusDefinition.key === 'paid' || payment.statusDefinition.key === 'paidOverdue') {
            acc.paidAmount += parseFloat(payment.value)
            acc.paidCount++
          } else if (payment.statusDefinition.key === 'pending') {
            acc.pendingAmount += parseFloat(payment.value)
            acc.pendingCount++
          } else if (payment.statusDefinition.text === 'Vencido') {
            acc.overdueAmount += parseFloat(payment.value)
            acc.overdueCount++
          }
          return acc
        },
        {
          totalAmount: 0,
          totalCount: 0,
          paidAmount: 0,
          paidCount: 0,
          pendingAmount: 0,
          pendingCount: 0,
          overdueAmount: 0,
          overdueCount: 0,
        },
      )
    },
  },
  watch: {
    searchBy(value) {
      this.getPayments()
    },
    // startDate(value) {
    //   clearTimeout(this.refreshTimeout)
    //   this.refreshTimeout = setTimeout(() => {
    //     this.getPayments()
    //   }, 2000)
    // },
    // endDate(value) {
    //   clearTimeout(this.refreshTimeout)
    //   this.refreshTimeout = setTimeout(() => {
    //     this.getPayments()
    //   }, 2000)
    // },
  },
  mounted() {
    this.startDate = moment().startOf('month').format('YYYY-MM-DD')
    this.endDate = moment().endOf('month').format('YYYY-MM-DD')
    this.getPayments()
  },
}
</script>
